import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {TableFieldCondition, TableFieldConditionArgumentOperator, TableFieldConditionOperator} from '../table-field.model';
import {MatCheckboxChange} from '@angular/material/checkbox';
import {TableFiledConditionOperatorsMap} from '../../../../locale/consts.en';
import {DECIMAL_PATTERN} from '../../../master-data/master-data.model';
import {
  ExpressionColumnInfo,
  ExpressionErrorStateMatcher,
  expressionSyntaxValidator,
  validateExpression,
} from '../expression-validator';

/** Data passed into this dialog via MAT_DIALOG_DATA. */
export interface TableFieldConditionDialogData {
  condition: TableFieldCondition | null;
  /** The table's columns, so the Expression field can be checked the same
   *  way as an EXPRESSION-type column. */
  columns: ExpressionColumnInfo[];
}

@Component({
    selector: 'app-table-filed-condition-dialog',
    templateUrl: './table-field-condition-dialog.component.html',
    styleUrls: ['./table-field-condition-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TableFieldConditionDialogComponent implements OnInit {
  private fb = inject(FormBuilder);
  dialogRef = inject<MatDialogRef<TableFieldConditionDialogComponent>>(MatDialogRef);
  data = inject<TableFieldConditionDialogData>(MAT_DIALOG_DATA);


  TableFieldConditionOp = TableFieldConditionOperator;
  TableFiledConditionOpMap =TableFiledConditionOperatorsMap;
  public conditionForm: FormGroup;
  expressionErrorMatcher = new ExpressionErrorStateMatcher();

  editMode = false;

  ngOnInit(): void {
    this.createConditionForm();
    this.setDataToForm();
    this.conditionForm.controls.expression.setValidators([
      Validators.required,
      expressionSyntaxValidator(() => this.data?.columns ?? []),
    ]);
    this.conditionForm.controls.expression.updateValueAndValidity();
  }

  private createConditionForm() {
    this.conditionForm = this.fb.group({
      expression: ['', [Validators.required]],
      operator: [+TableFieldConditionOperator.OR, [Validators.required]],
      oneArgument: [true],
      argument1: this.fb.group({
        value: [null, [Validators.required, Validators.pattern(DECIMAL_PATTERN)]],
        operator: [+TableFieldConditionArgumentOperator.EQ, [Validators.required]]
      }),
      argument2: this.fb.group({
        value: [null, [Validators.pattern(DECIMAL_PATTERN)]],
        operator: [+TableFieldConditionArgumentOperator.EQ]
      }),
      resultTrue: [null, [Validators.required]],
      resultFalse: [null, [Validators.required]]
    });
  }

  setDataToForm() {
    const condition = this.data?.condition;
    if (condition != null) {
      this.editMode = true;
      this.conditionForm.patchValue({
        expression: condition.expression,
        operator: condition.operator,
        oneArgument: condition.oneArgument,
        resultTrue: condition.resultTrue,
        resultFalse: condition.resultFalse,
        argument1: {
          value: condition.argument1.value,
          operator: condition.argument1.operator
        }
        }
      );
      if(!condition.oneArgument) {
        this.conditionForm.patchValue({
          argument2: {
            value: condition.argument2.value,
            operator: condition.argument2.operator
          }
        });
      }
    }
    this.setDisableOnArg2Operator();
  }

  /** Re-runs the expression validator on every keystroke - relying solely
   *  on the reactive-forms binding left the mat-error stale until some
   *  other event (e.g. a click elsewhere in the dialog) forced a refresh. */
  onExpressionInput(): void {
    this.conditionForm.controls.expression.updateValueAndValidity();
  }

  /** Non-blocking heads-up for the currently-typed expression (e.g. a
   *  DROPDOWN column referenced without every option mapped to a number). */
  expressionWarnings(): string[] {
    return validateExpression(
      this.conditionForm.controls.expression.value,
      this.data?.columns ?? [],
    ).warnings;
  }

  onCloseClick(): void {
    this.dialogRef.close();
  }

  onSave() {
    this.dialogRef.close(this.conditionForm.value);
  }

  onOneArgChange(e: MatCheckboxChange) {
    if (e.checked) {
      (this.conditionForm.controls.argument2 as FormGroup).controls.value.setValidators([
        Validators.pattern(DECIMAL_PATTERN),
      ]);
      (this.conditionForm.controls.argument2 as FormGroup).controls.operator.setValidators([]);
    } else {
      (this.conditionForm.controls.argument2 as FormGroup).controls.value.setValidators([
        Validators.required,
        Validators.pattern(DECIMAL_PATTERN),
      ]);
      (this.conditionForm.controls.argument2 as FormGroup).controls.operator.setValidators(Validators.required);
    }
    (this.conditionForm.controls.argument2 as FormGroup).controls.value.updateValueAndValidity();
    (this.conditionForm.controls.argument2 as FormGroup).controls.operator.updateValueAndValidity();
    this.setDisableOnArg2Operator();
  }

  setDisableOnArg2Operator() {
    if(this.conditionForm.controls.oneArgument.value) {
      (this.conditionForm.controls.argument2 as FormGroup).controls.operator.disable({onlySelf: true});
    } else {
      (this.conditionForm.controls.argument2 as FormGroup).controls.operator.enable({onlySelf: true});
    }
  }
}
